/**
 * Declare a new box from the canvas: a state, or a step of a function.
 *
 * Where it goes is decided by what is selected, not asked: it is created INSIDE
 * the selected box - a state inside a state makes that one a machine, a step
 * inside a function decomposes it - or, with nothing selected, beside the view
 * in its package. Asking for a package here would offer places the diagram
 * cannot show.
 *
 * A STEP is a usage, so it is given the function it performs. A state is a
 * definition and takes its type from the kind's default.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { elementOption, useElements } from '../common/elements'

export default function NewStateDialog({
  inside,
  noun = 'state',
  plural = 'states',
  typed = false,
  onClose,
  onCreated,
}: {
  /** The qualified name of the element or package the new box is declared in. */
  inside: string
  /** 'state' or 'step'. */
  noun?: string
  /** The API plural it is created through. */
  plural?: string
  /** Whether it is a usage typed by a definition, which must then be chosen. */
  typed?: boolean
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const definitions = useElements(undefined, typed ? plural : undefined)
  const type = definitions.rows.find((row) => row.id === typeId) ?? null

  const submit = () => {
    setBusy(true)
    setFailure('')
    const body: Record<string, unknown> = { package: inside, identifier, ...(name ? { name } : {}) }
    if (typed) {
      body.type_name = String(type?.name || type?.id || '')
      body.is_definition = false
    }
    api
      .create(plural, body)
      .then((created) => onCreated(created.identifier))
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New {noun}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Declared in <span style={{ fontFamily: MONOSPACE }}>{inside}</span>
          </Typography>
          <TextField
            label="ID"
            required
            size="small"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            helperText={noun === 'step' ? 'The short name, e.g. STEP_HOLD' : 'The short name, e.g. ST_SAFE'}
            slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
          />
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            helperText={`What ${noun === 'step' ? 'successions' : 'transitions'} name it by; defaults to the ID`}
          />
          {typed && (
            <ElementPicker
              rows={definitions.rows}
              where={(row) => row.is_definition !== false}
              describe={(row, value) => ({ ...elementOption(row, value), chosen: `${row.name || row.id} (${row.id})` })}
              value={typeId || null}
              onChange={setTypeId}
              label="Performs"
              required
              helperText="The function this step is a use of"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!identifier || (typed && !type) || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
